import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "../../admin/ui.tsx";
import { lengthState } from "./messages.ts";

interface Props {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: "current-password" | "new-password";
  name?: string;
  hint?: string;
  error?: string | undefined;
  /** Shows the live length check under the field (new passwords only). */
  lengthCheck?: boolean;
}

/** Password input with a show/hide toggle (a real 44px button, announced as pressed). */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  name,
  hint,
  error,
  lengthCheck,
}: Props) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  return (
    <Field label={label} hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <>
          <div className="login-pw">
            <input
              id={id}
              name={name ?? (autoComplete === "current-password" ? "password" : "new-password")}
              className="adm-input"
              type={shown ? "text" : "password"}
              autoComplete={autoComplete}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
            <button
              type="button"
              className="ds-btn ds-btn--plain login-pw__toggle"
              aria-pressed={shown}
              aria-label={shown ? t("diver.auth.hidePassword") : t("diver.auth.showPassword")}
              onClick={() => setShown(!shown)}
            >
              {shown ? (
                <EyeOff aria-hidden="true" strokeWidth={1.75} />
              ) : (
                <Eye aria-hidden="true" strokeWidth={1.75} />
              )}
            </button>
          </div>
          {lengthCheck ? <LengthCheck value={value} /> : null}
        </>
      )}
    </Field>
  );
}

/** "Noch 4 Zeichen" / "Länge passt": a polite live region, so it is read once the user pauses, not per key. */
export function LengthCheck({ value }: { value: string }) {
  const { t } = useTranslation();
  const { state, missing } = lengthState(value);
  const text =
    state === "empty"
      ? t("diver.auth.lengthHint")
      : state === "short"
        ? t("diver.auth.lengthShort", { count: missing })
        : state === "long"
          ? t("diver.auth.lengthLong")
          : t("diver.auth.lengthOk", { count: value.length });
  return (
    <p
      className="login-length ds-text-footnote m-0"
      data-state={state}
      role="status"
      aria-live="polite"
    >
      {text}
    </p>
  );
}
